import { DeviceTypes, RichDevice } from '@store';
import {
  Device,
  DeviceIds,
  DeviceMonetizationFeatures,
  GatewayMonetizationFeatures,
  GetResponse,
  Installation,
  InstallationMonetizationFeatures,
  MonetizationFeature,
  ViCarePlan,
} from '@vi-iot/client';
import {
  hasMainDevice,
  isLeadCascadeV1Device,
  isLegacyGateway,
} from './installation-details-helpers';
import { ViCarePlans } from './installation-details.model';

export class InstallationDetailsMappers {
  static uniqueViCarePlans(plans: ViCarePlan<string>[]): ViCarePlan<string>[] {
    const basic = plans.find((plan) => plan.plan === ViCarePlans.basic);

    if (!basic) {
      return plans;
    }

    return plans.map((plan) => {
      if (plan.plan !== ViCarePlans.basic) {
        return {
          ...plan,
          features: plan.features.filter((feature) => !basic.features.includes(feature)),
        };
      }

      return plan;
    });
  }

  static mapMonetizationFeaturesResponse(
    response: GetResponse<InstallationMonetizationFeatures>,
    selectedDevice: RichDevice
  ): Record<string, MonetizationFeature> {
    const installationMonetizationFeatures = response.data.installations.reduce(
      (installationAcc, installation) => {
        const selectedGateway = installation.gateways.filter(
          (gateway: GatewayMonetizationFeatures) =>
            gateway.gatewaySerial === selectedDevice?.gatewaySerial
        );
        const gatewayDeviceMonetizationFeatures = InstallationDetailsMappers.getMonetizationFeatuesFromGateways(
          selectedGateway
        );

        return [
          ...installationAcc,
          ...gatewayDeviceMonetizationFeatures,
          ...installation.monetizationFeatures,
        ];
      },
      []
    );

    return InstallationDetailsMappers.mergeSupportedFeatures(installationMonetizationFeatures);
  }

  static mapInstallationResponse(installation: Installation): Installation {
    return {
      ...installation,
      gateways: installation?.gateways?.map((gateway) => {
        const mainDevice = gateway.devices.find(
          (device) =>
            device.id === DeviceIds.Main || device.deviceType === DeviceTypes.SystemController
        );
        const gatewayDevice = gateway.devices.find(
          (device) =>
            device.id === DeviceIds.Gateway ||
            device.id === DeviceIds.TcuAlternative ||
            device.id === DeviceIds.Tcu
        );
        const mainDeviceOfGateway = mainDevice ? mainDevice : gatewayDevice;

        return {
          ...gateway,
          devices: gateway.devices.map((device) => {
            const isMainDevice = mainDeviceOfGateway.id === device.id;

            return {
              ...device,
              isStandalone: !hasMainDevice(gateway) && isLegacyGateway(device),
              isMainDeviceOfGateway: isMainDevice,
              isSelected: isMainDevice,
              isLeadDeviceInCascades: isLeadCascadeV1Device(device),
            };
          }),
        };
      }),
    };
  }

  static mapAllDevices(installation: Installation): Device[] {
    return installation.gateways.reduce(
      (allGateways, gateway) => [...allGateways, ...gateway.devices],
      []
    );
  }

  static mapToDevicesWithFeatures(devices: Device[]): RichDevice[] {
    let idx = 0;

    return devices?.map((device: Device) => {
      const deviceWithFeatures: RichDevice = {
        ...(device as RichDevice), // TODO: This type enforcement shows typing issue potentialy
        selectId: (++idx).toString(),
        loading: false,
        loaded: false,
      };

      return deviceWithFeatures;
    });
  }

  private static getMonetizationFeatuesFromGateways(
    gatewayFeatures: GatewayMonetizationFeatures[]
  ): MonetizationFeature[] {
    return gatewayFeatures.reduce((gatewayAcc, gateway) => {
      return [
        ...gatewayAcc,
        ...InstallationDetailsMappers.getMonetizationFeaturesFromDevices(gateway.devices),
        ...gateway.monetizationFeatures,
      ];
    }, []);
  }

  private static getMonetizationFeaturesFromDevices(
    deviceFeatures: DeviceMonetizationFeatures[]
  ): MonetizationFeature[] {
    return deviceFeatures.reduce((deviceAcc, device) => {
      return [...deviceAcc, ...device.monetizationFeatures];
    }, []);
  }

  private static mergeSupportedFeatures(
    features: MonetizationFeature[]
  ): Record<string, MonetizationFeature> {
    return features.reduce((acc, feature) => {
      if (acc[feature.name] && acc[feature.name].supported) {
        return acc;
      }
      acc[feature.name] = feature;

      return acc;
    }, {});
  }
}
